<template>
  <section>
    <h2>AI 編碼</h2>
    <fieldset :disabled="isSavingResult">
      <b-form-group label="選擇準則庫" label-for="qa-ai-criterion">
        <b-form-select
          id="qa-ai-criterion"
          v-model="selectedCriterionId"
          :disabled="isLoadingCriterion || isCoding"
          @change="loadCriterion"
        >
          <b-form-select-option :value="null" disabled>
            請選擇準則庫
          </b-form-select-option>
          <b-form-select-option
            v-for="entry in criteria"
            :key="entry.id"
            :value="entry.id"
          >
            {{ entry.name }}
          </b-form-select-option>
        </b-form-select>
      </b-form-group>
      <b-alert :show="Boolean(criterionError)" variant="danger">
        {{ criterionError }}
      </b-alert>
      <div v-if="criterion.id && !isLoadingCriterion">
        <p>準則庫：{{ criterion.name }}</p>
        <p>題數：{{ itemCount }}</p>
        <p>預估 AI 呼叫次數：{{ estimatedCalls }}</p>

        <b-form-group
          label="額外判斷說明（選填）"
          label-for="qa-ai-instruction"
        >
          <b-form-textarea
            id="qa-ai-instruction"
            v-model="extraInstruction"
            rows="3"
            :disabled="isCoding"
          />
        </b-form-group>

        <b-form-group label="溫度" label-for="qa-ai-temperature">
          <b-form-input
            id="qa-ai-temperature"
            v-model.number="temperature"
            type="number"
            step="0.1"
            :disabled="isCoding"
          />
        </b-form-group>

        <b-button
          variant="primary"
          :disabled="isCoding || isLoadingCriterion"
          @click="startCoding"
        >
          <b-icon icon="play-fill" aria-hidden="true" />
          {{ isCoding ? "編碼進行中…" : "開始 AI 編碼" }}
        </b-button>
        <b-button
          v-if="batchId"
          variant="outline-secondary"
          class="ml-2"
          :disabled="isCoding || isLoadingCriterion"
          @click="resumeCoding"
        >
          <b-icon icon="arrow-clockwise" aria-hidden="true" />
          重新讀取任務
        </b-button>
        <b-button
          v-if="batchId && summary.failed > 0"
          variant="outline-danger"
          class="ml-2"
          :disabled="isCoding || isLoadingCriterion || !isBatchComplete"
          @click="retryFailed"
        >
          <b-icon icon="arrow-repeat" aria-hidden="true" />
          重試失敗題目
        </b-button>
        <b-alert :show="Boolean(codingError)" variant="danger" class="mt-3">
          {{ codingError }}
        </b-alert>
        <p v-if="batchId">任務編號：{{ batchId }}</p>
        <b-alert :show="Boolean(batchStorageError)" variant="warning">
          {{ batchStorageError }}
        </b-alert>

        <h3>任務進度（測試資料）</h3>
        <p>已結束 {{ finishedCount }} / {{ summary.total }} 題</p>
        <b-progress :value="finishedCount" :max="summary.total" class="mb-3" />
        <dl>
          <dt>等待中</dt>
          <dd>{{ summary.pending }}</dd>

          <dt>執行中</dt>
          <dd>{{ summary.running }}</dd>

          <dt>成功</dt>
          <dd>{{ summary.done }}</dd>

          <dt>失敗</dt>
          <dd>{{ summary.failed }}</dd>

          <dt>已取消</dt>
          <dd>{{ summary.cancelled }}</dd>
        </dl>

        <h3>編碼結果（測試資料）</h3>
        <b-table :items="resultRows" :fields="resultFields" responsive />
        <b-form-group
          label="結果名稱"
          label-for="qa-ai-result-name"
          class="mt-3"
        >
          <b-form-input
            id="qa-ai-result-name"
            v-model.trim="resultName"
            maxlength="128"
          />
        </b-form-group>

        <b-alert :show="Boolean(saveError)" variant="warning">
          {{ saveError }}
        </b-alert>

        <b-button
          variant="primary"
          :disabled="
            Boolean(saveError) || isSavingResult || Boolean(savedResultId)
          "
          @click="saveAiResult"
        >
          <b-icon icon="check-circle" aria-hidden="true" />
          {{ isSavingResult ? "儲存中…" : "儲存 AI 結果" }}
        </b-button>
        <b-alert
          :show="Boolean(saveRequestError)"
          variant="danger"
          class="mt-3"
        >
          {{ saveRequestError }}
        </b-alert>
        <b-alert :show="Boolean(savedResultId)" variant="success" class="mt-3">
          AI 結果已儲存，結果 ID：{{ savedResultId }}
        </b-alert>
      </div>
    </fieldset>
  </section>
</template>

<script>
import axios from "axios";
import { pollBatch } from "@/utils/jobPolling";
export default {
  name: "QAAiRun",

  data() {
    return {
      extraInstruction: "",
      temperature: 0,
      resultName: "",
      resultFields: [
        { key: "no", label: "編號" },
        { key: "text", label: "文本" },
        { key: "result", label: "結果" },
      ],
      criterion: {
        id: null,
        name: "",
        codingType: "",
        scale: null,
        labels: [],
        items: [],
      },
      criteria: [],
      selectedCriterionId: null,
      isLoadingCriterion: false,
      criterionError: "",
      summary: {
        total: 0,
        pending: 0,
        running: 0,
        done: 0,
        failed: 0,
        cancelled: 0,
      },
      codes: {},
      failedNos: [],
      batchId: null,
      isBatchComplete: false,
      isCoding: false,
      codingError: "",
      isSavingResult: false,
      saveRequestError: "",
      savedResultId: null,
      batchStorageError: "",
    };
  },

  methods: {
    async loadCriteria() {
      if (this.isLoadingCriterion || this.isCoding || this.isSavingResult)
        return;

      this.isLoadingCriterion = true;
      this.criterionError = "";
      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const response = await axios.get(
          `${base.replace(/\/+$/, "")}/api/qa/criteria`,
        );
        this.criteria = response.data;
      } catch (error) {
        this.criterionError =
          error.response?.data?.message || error.message || "讀取失敗";
      } finally {
        this.isLoadingCriterion = false;
      }
    },
    async loadCriterion(id) {
      if (this.isSavingResult) return;
      if (!id || this.isLoadingCriterion || this.isCoding) return;
      this.isLoadingCriterion = true;
      this.criterionError = "";
      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const response = await axios.get(
          `${base.replace(/\/+$/, "")}/api/qa/criteria/${id}`,
        );
        this.criterion = response.data;
        this.savedResultId = null;
        this.saveRequestError = "";
        this.batchId = null;
        this.isBatchComplete = false;
        this.codingError = "";
        this.codes = {};
        this.failedNos = [];
        this.resultName = "";
        this.extraInstruction = "";
        this.temperature = 0;
        this.summary = {
          total: this.criterion.items.length,
          pending: 0,
          running: 0,
          done: 0,
          failed: 0,
          cancelled: 0,
        };
        this.restoreBatchReference();
      } catch (error) {
        this.selectedCriterionId = this.criterion.id;
        this.criterionError =
          error.response?.data?.message || error.message || "讀取失敗";
      } finally {
        this.isLoadingCriterion = false;
      }
    },
    async startCoding() {
      if (this.batchStorageError) return;
      if (this.isSavingResult) return;
      if (!this.criterion.id || this.isLoadingCriterion || this.isCoding)
        return;
      if (this.batchId && !this.isBatchComplete) {
        this.codingError = "已有尚未確認結束的任務，請先重新讀取任務";
        return;
      }

      this.isCoding = true;
      this.codingError = "";

      try {
        if (!Number.isFinite(this.temperature) || this.temperature < 0) {
          throw new Error("溫度必須是非負的有效數字");
        }
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `將建立 ${this.itemCount} 題 AI 任務，執行時會產生費用，確定開始？`,
          { title: "開始 AI 編碼", okTitle: "開始", cancelTitle: "取消" },
        );
        if (!confirmed || this._qaDisposed) return;

        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const url = base.replace(/\/+$/, "");
        this.codes = {};
        this.failedNos = [];
        this.resultName = "";
        this.savedResultId = null;
        this.saveRequestError = "";
        const response = await axios.post(
          `${url}/api/qa/criteria/${this.criterion.id}/ai-code`,
          {
            extraInstruction: this.extraInstruction,
            temperature: this.temperature,
          },
        );
        this.batchId = response.data.batchId;
        this.isBatchComplete = false;
        this.saveBatchReference();
        if (this._qaDisposed) return;
        await this.waitForBatch(url);
      } catch (error) {
        if (!this._qaDisposed) {
          this.codingError =
            error.response?.data?.message ||
            error.message ||
            "AI 編碼或進度讀取失敗";
        }
      } finally {
        if (this._qaPoll) this._qaPoll.cancel();
        this._qaPoll = null;
        this.isCoding = false;
      }
    },
    async waitForBatch(url) {
      this._qaPoll = pollBatch(this.batchId, {
        onUpdate: (summary) => {
          this.summary = summary;
        },
      });
      const finished = await this._qaPoll.promise;
      if (!finished || this._qaDisposed) return;

      const result = await axios.get(
        `${url}/api/qa/ai-code/${encodeURIComponent(this.batchId)}`,
      );
      if (this._qaDisposed) return;
      this.codes = result.data.codes;
      this.failedNos = result.data.failedNos;
      this.isBatchComplete = result.data.done === true;
    },
    async resumeCoding() {
      if (this.isSavingResult) return;
      if (!this.batchId || this.isCoding || this.isLoadingCriterion) return;
      this.isCoding = true;
      this.codingError = "";
      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        await this.waitForBatch(base.replace(/\/+$/, ""));
      } catch (error) {
        if (!this._qaDisposed) {
          this.codingError =
            error.response?.data?.message || error.message || "讀取任務失敗";
        }
      } finally {
        if (this._qaPoll) this._qaPoll.cancel();
        this._qaPoll = null;
        this.isCoding = false;
      }
    },
    async retryFailed() {
      if (this.isSavingResult) return;
      if (
        !this.batchId ||
        !this.isBatchComplete ||
        this.isCoding ||
        this.isLoadingCriterion ||
        this.summary.failed <= 0
      )
        return;

      this.isCoding = true;
      this.codingError = "";
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `將重試失敗任務，執行時會產生 AI 費用。確定繼續？`,
          { title: "重試失敗題目", okTitle: "重試", cancelTitle: "取消" },
        );
        if (!confirmed || this._qaDisposed) return;

        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const url = base.replace(/\/+$/, "");

        this.isBatchComplete = false;
        await axios.post(
          `${url}/api/jobs/batch/${encodeURIComponent(
            this.batchId,
          )}/retry-failed`,
        );
        if (this._qaDisposed) return;

        this.codes = {};
        this.failedNos = [];
        await this.waitForBatch(url);
      } catch (error) {
        if (!this._qaDisposed) {
          this.codingError =
            error.response?.data?.message ||
            error.message ||
            "重試或進度讀取失敗，請先重新讀取任務";
        }
      } finally {
        if (this._qaPoll) this._qaPoll.cancel();
        this._qaPoll = null;
        this.isCoding = false;
      }
    },
    async saveAiResult() {
      if (this.saveError || this.isSavingResult || this.savedResultId) return;
      this.isSavingResult = true;
      this.saveRequestError = "";

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const response = await axios.post(
          `${base.replace(/\/+$/, "")}/api/qa/results`,
          {
            criterionId: this.criterion.id,
            name: this.resultName.trim(),
            batchId: this.batchId,
            isAI: true,
            content: { source: "ai" },
            note: null,
          },
        );
        if (!this._qaDisposed) {
          this.savedResultId = response.data.id;
          this.saveBatchReference();
        }
      } catch (error) {
        if (!this._qaDisposed) {
          this.saveRequestError =
            error.response?.data?.message ||
            error.message ||
            "儲存 AI 結果失敗";
        }
      } finally {
        this.isSavingResult = false;
      }
    },

    saveBatchReference() {
      if (!this.criterion.id || !this.batchId) return;
      try {
        localStorage.setItem(
          `qa-ai-batch:${this.criterion.id}`,
          JSON.stringify({
            batchId: this.batchId,
            savedResultId: this.savedResultId,
            resultName: this.resultName,
          }),
        );
        this.batchStorageError = "";
      } catch {
        this.batchStorageError = "無法保存任務編號，離頁前請記下 batch ID";
      }
    },
    restoreBatchReference() {
      this.batchStorageError = "";
      try {
        const raw = localStorage.getItem(`qa-ai-batch:${this.criterion.id}`);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (
          !saved ||
          typeof saved.batchId !== "string" ||
          !saved.batchId.trim() ||
          (saved.savedResultId != null &&
            (!Number.isSafeInteger(saved.savedResultId) ||
              saved.savedResultId <= 0))
        )
          throw new Error("記錄格式不正確");
        this.batchId = saved.batchId;
        this.savedResultId = saved.savedResultId ?? null;
        this.resultName =
          typeof saved.resultName === "string" ? saved.resultName : "";
        this.isBatchComplete = false;
      } catch {
        this.batchStorageError =
          "無法恢復任務記錄，請先確認原任務，勿直接重新開始";
      }
    },
  },

  computed: {
    itemCount() {
      return this.criterion.items.length;
    },
    finishedCount() {
      return this.summary.done + this.summary.failed + this.summary.cancelled;
    },

    resultRows() {
      const failed = new Set(this.failedNos.map(String));

      return this.criterion.items.map((item) => {
        const key = String(item.no);
        const isFailed = failed.has(key);
        const hasCode = Object.prototype.hasOwnProperty.call(this.codes, key);

        return {
          no: item.no,
          text: item.text,
          result: isFailed ? "失敗" : hasCode ? this.codes[key] : "尚無結果",
          _rowVariant: isFailed ? "danger" : null,
        };
      });
    },

    estimatedCalls() {
      return this.itemCount;
    },

    saveError() {
      if (!this.batchId) return "尚未建立 AI 編碼任務";

      if (this.isCoding || (this.batchId && !this.isBatchComplete)) {
        return "任務尚未確認完成，不可儲存";
      }
      if (this.codingError) {
        return "請先重新讀取任務，確認完整結果";
      }

      if (!this.resultName.trim()) {
        return "請填寫結果名稱";
      }

      if (this.resultName.trim().length > 128) {
        return "結果名稱不可超過 128 個字元";
      }

      if (!this.criterion.items.length) {
        return "沒有題目可以儲存";
      }

      if (this.summary.pending > 0 || this.summary.running > 0) {
        return "AI 編碼尚未結束";
      }

      if (this.summary.failed > 0 || this.failedNos.length > 0) {
        return "有失敗題目，請先重試";
      }

      if (this.summary.cancelled > 0) {
        return "有取消的題目，結果不完整";
      }

      const complete = this.criterion.items.every((item) =>
        Object.prototype.hasOwnProperty.call(this.codes, String(item.no)),
      );

      return complete ? "" : "尚未取得全部題目的結果";
    },
  },
  mounted() {
    this.loadCriteria();
  },
  beforeDestroy() {
    this._qaDisposed = true;
    if (this._qaPoll) this._qaPoll.cancel();
  },
};
</script>
